import React, { FunctionComponent, useState } from 'react';
import { FastForward } from 'lucide-react';
import { Button, Dialog, DialogContent, DialogTrigger } from '@companion-professional/components';
import { CheckupTag } from '@companion-professional/dctypes';
import { CopyUrlContent } from './CopyUrlContent';
import { CheckupData, CreateQuickCheckup } from './CreateQuickCheckup';
import { AddPetName } from './AddPetName';

interface QuickCheckupProps {
  // autofillPetId is the pet id that the user entered in when using the QuickCheckup to create a checkup (we assume
  // that this comes from the clinic's PIMs system, but in practice this could be anything).  If this is provided, it
  // will be autofilled into the pet id field of the quick checkup dialog so the user doesn't have to type it in.
  autofillPetId?: string;
}

// PendingFormValues holds the values from step 1 that are needed to retry checkup creation in step 2 once the user
// has provided the new pet's name and species.
type PendingFormValues = {
  clinicPetId: string;
  locationTag: CheckupTag;
};

// QuickCheckup is an all-in-one quick checkup dialog that allows the user to create a new checkup request with just
// the clinic pet id (ie what comes from a PIMs system).  They can then copy the link to the clipboard and share it
// with their client (pet parent).
export const QuickCheckup: FunctionComponent<QuickCheckupProps> = ({ autofillPetId }) => {
  const [open, setOpen] = useState(false);
  const [checkupData, setCheckupData] = useState<CheckupData | null>(null);

  // pendingFormValues is set when the pet was not found in step 1. It holds the form values needed to retry checkup
  // creation in step 2 along with the new pet's name and species.
  const [pendingFormValues, setPendingFormValues] = useState<PendingFormValues | null>(null);

  return (
    <Dialog
      open={open}
      onOpenChange={(openChange) => {
        setCheckupData(null);
        setPendingFormValues(null);
        setOpen(openChange);
      }}
    >
      <DialogTrigger asChild>
        <Button variant="primary" size="small">
          <FastForward className="size-5" />
          &nbsp;Quick Checkup
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-md">
        {checkupData ? (
          <CopyUrlContent checkupData={checkupData} />
        ) : pendingFormValues ? (
          <AddPetName pendingFormValues={pendingFormValues} setCheckupData={setCheckupData} />
        ) : (
          <CreateQuickCheckup
            setCheckupData={setCheckupData}
            onPetNotFound={setPendingFormValues}
            autofillPetId={autofillPetId}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
